import { useEffect, useMemo, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Button, Drawer, Dropdown, Layout, Menu } from "antd";
import {
  CloseOutlined,
  DownOutlined,
  HomeOutlined,
  LockOutlined,
  MenuFoldOutlined,
  MenuOutlined,
  MenuUnfoldOutlined,
  LogoutOutlined,
  MedicineBoxOutlined,
  QrcodeOutlined,
  SafetyCertificateOutlined,
  ShopOutlined,
  TeamOutlined,
  TransactionOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { STAFF_ACCESS_ROLES } from "../../api/auth";
import { getCareMainHeader } from "../../config/careMainHeader";
import { logout } from "../../store/authSlice";
import UserAvatar from "../UserAvatar";
import "./style.less";

const { Sider, Content } = Layout;

const LOGO_SRC = `${process.env.PUBLIC_URL}/logo.png`;

const BASE_MENU_ITEMS = [
  { key: "/", icon: <HomeOutlined />, label: "Overview" },
  { key: "/doctors", icon: <MedicineBoxOutlined />, label: "Doctors" },
  { key: "/patients", icon: <TeamOutlined />, label: "Patients" },
  { key: "/pharmacy", icon: <ShopOutlined />, label: "Pharmacy" },
  { key: "/transactions", icon: <TransactionOutlined />, label: "Transactions" },
  { key: "/qr-manage", icon: <QrcodeOutlined />, label: "QR manage" },
  // { key: "/support-logs", icon: <FileSearchOutlined />, label: "Support Logs" },
];

function collectMenuItemKeys(items) {
  const keys = new Set();
  for (const item of items) {
    if (!item || typeof item !== "object") continue;
    if (item.key) keys.add(item.key);
    if (Array.isArray(item.children)) {
      for (const child of item.children) {
        if (child?.key) keys.add(child.key);
      }
    }
  }
  return keys;
}

/** Match Ant Design Layout Sider `breakpoint="lg"` (see antd dimensionMaxMap). */
const MOBILE_NAV_MEDIA = "(max-width: 991.98px)";

function useMobileNavLayout() {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia(MOBILE_NAV_MEDIA).matches : false,
  );

  useEffect(() => {
    const mql = window.matchMedia(MOBILE_NAV_MEDIA);
    const onChange = () => setIsMobile(mql.matches);
    if (typeof mql.addEventListener === "function") {
      mql.addEventListener("change", onChange);
    } else {
      mql.addListener(onChange);
    }
    setIsMobile(mql.matches);
    return () => {
      if (typeof mql.removeEventListener === "function") {
        mql.removeEventListener("change", onChange);
      } else {
        mql.removeListener(onChange);
      }
    };
  }, []);

  return isMobile;
}

export default function CareLayout() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector((state) => state.auth.user);
  const hasStaffAccess = STAFF_ACCESS_ROLES.includes(user?.role);
  const isMobileNav = useMobileNavLayout();
  const [siderCollapsed, setSiderCollapsed] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  const handleLogout = () => {
    dispatch(logout());
  };

  const displayName = user?.name?.trim() || user?.email || "Account";
  const showEmailSubtitle = Boolean(user?.name?.trim() && user?.email);

  const menuItems = useMemo(() => {
    const items = [...BASE_MENU_ITEMS];
    if (hasStaffAccess) {
      items.push({ type: "divider", className: "care-nav-private-divider" });
      items.push({
        type: "group",
        label: (
          <span className="care-nav-private-heading">
            <LockOutlined className="care-nav-private-heading-icon" aria-hidden />
            <span className="care-nav-private-heading-text">Private</span>
          </span>
        ),
        children: [
          {
            key: "/staff-access",
            icon: <SafetyCertificateOutlined />,
            label: "Staff Access",
          },
        ],
      });
    }
    return items;
  }, [hasStaffAccess]);

  const menuKeys = useMemo(() => collectMenuItemKeys(menuItems), [menuItems]);

  const selectedKeys = useMemo(() => {
    const path = location.pathname;
    if (path === "/") {
      return ["/"];
    }
    if (menuKeys.has(path)) {
      return [path];
    }
    return [];
  }, [location.pathname, menuKeys]);

  const closeMobileDrawer = () => setMobileDrawerOpen(false);

  const onMenuClick = ({ key }) => {
    navigate(key);
    if (isMobileNav) {
      closeMobileDrawer();
    }
  };

  useEffect(() => {
    setMobileDrawerOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isMobileNav) {
      setMobileDrawerOpen(false);
    }
  }, [isMobileNav]);

  const mainHeader = useMemo(() => getCareMainHeader(location.pathname), [location.pathname]);

  const accountDropdown = (
    <Dropdown
      menu={{
        items: [
          {
            key: "profile",
            label: "Profile",
            icon: <UserOutlined />,
          },
          { type: "divider" },
          {
            key: "logout",
            label: "Sign out",
            danger: true,
            icon: <LogoutOutlined />,
          },
        ],
        onClick: ({ key }) => {
          if (key === "profile") {
            navigate("/profile");
            if (isMobileNav) {
              closeMobileDrawer();
            }
          }
          if (key === "logout") {
            handleLogout();
          }
        },
      }}
      trigger={["click"]}
      placement="topLeft"
      classNames={{ root: "care-account-dropdown" }}
    >
      <Button type="text" className="care-account-menu-trigger" block>
        <span className="care-account-menu-trigger-inner">
          <UserAvatar user={user} size={40} className="care-account-avatar" />
          <span className="care-account-menu-trigger-body">
            <span className="care-account-menu-trigger-name">{displayName}</span>
            {showEmailSubtitle ? (
              <span className="care-account-menu-trigger-email">{user.email}</span>
            ) : null}
          </span>
          <DownOutlined className="care-account-chevron" />
        </span>
      </Button>
    </Dropdown>
  );

  const renderSideNav = (headerAction, menuInlineCollapsed) => (
    <>
      <div className="care-sider-header">
        <Link to="/" className="care-sider-brand">
          <img src={LOGO_SRC} alt="" className="care-sider-logo-img" width={40} height={40} />
          <span className="care-sider-brand-text">
            <span className="care-sider-brand-name">Nvoisys Health</span>
            <span className="care-sider-brand-sub">Customer care</span>
          </span>
        </Link>
        {headerAction}
      </div>
      <Menu
        mode="inline"
        inlineCollapsed={menuInlineCollapsed}
        selectedKeys={selectedKeys}
        items={menuItems}
        onClick={onMenuClick}
        className="care-sider-menu"
      />
      <div className="care-sider-footer">{accountDropdown}</div>
    </>
  );

  const desktopSiderToggle = (
    <Button
      type="text"
      className="care-sider-header-toggle"
      aria-expanded={!siderCollapsed}
      aria-label={siderCollapsed ? "Expand navigation" : "Collapse navigation"}
      icon={siderCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
      onClick={() => setSiderCollapsed((c) => !c)}
    />
  );

  const mobileDrawerClose = (
    <Button
      type="text"
      className="care-sider-header-toggle care-sider-header-toggle--close"
      aria-label="Close menu"
      icon={<CloseOutlined />}
      onClick={closeMobileDrawer}
    />
  );

  return (
    <Layout
      className={`care-app-layout${isMobileNav ? " care-app-layout--mobile-nav" : ""}`}
      hasSider={!isMobileNav}
    >
      {!isMobileNav ? (
        <Sider
          theme="light"
          width={268}
          collapsedWidth={80}
          collapsible
          collapsed={siderCollapsed}
          onCollapse={setSiderCollapsed}
          trigger={null}
          className={`care-sider${siderCollapsed ? " care-sider--collapsed" : ""}`}
          aria-label="Application"
        >
          {renderSideNav(desktopSiderToggle, siderCollapsed)}
        </Sider>
      ) : null}
      <Layout className="care-app-main">
        <header className="care-main-header">
          <div className="care-main-header-top">
            {isMobileNav ? (
              <Button
                type="text"
                className="care-main-header-burger"
                aria-label="Open navigation menu"
                aria-expanded={mobileDrawerOpen}
                icon={<MenuOutlined />}
                onClick={() => setMobileDrawerOpen(true)}
              />
            ) : null}
            <div className="care-main-header-copy">
              <div className="care-main-header-title-row">
                <span className="care-main-header-mark" aria-hidden>
                  ✦
                </span>
                <h1 className="care-main-header-title">{mainHeader.title}</h1>
              </div>
              {mainHeader.lead ? (
                <div className="care-main-header-lead">{mainHeader.lead}</div>
              ) : null}
            </div>
          </div>
        </header>
        <Content className="care-main">
          <Outlet />
        </Content>
      </Layout>
      {isMobileNav ? (
        <Drawer
          placement="left"
          size="min(20.5rem, 92vw)"
          open={mobileDrawerOpen}
          onClose={closeMobileDrawer}
          className="care-mobile-nav-drawer"
          closable={false}
          styles={{
            body: {
              padding: 0,
              display: "flex",
              flexDirection: "column",
              height: "100%",
              overflow: "hidden",
            },
          }}
        >
          <div className="care-sider-panel">{renderSideNav(mobileDrawerClose, false)}</div>
        </Drawer>
      ) : null}
    </Layout>
  );
}
